<template>
    <div class="ticket-price__input-wrapper">
        <div
            class="ticket-price__input"
            :class="{ 'ticket-price__input--invalid': inputInvalid }"
            @mousedown.capture="handleClickCapture"
            @click.capture="handleClickCapture"
        >
            <input
                v-model="inputValue"
                ref="inputElem"
                type="text"
                inputmode="decimal"
                :id="inputId"
                :name="inputId"
                class="ticket-price__input-inp"
                :class="[
                    { 'ticket-price__input-inp--valid': inputValue?.length },
                    { 'ticket-price__input-inp--invalid': inputInvalid },
                ]"
                autocomplete="off"
                @click="selectAll"
                @blur="handleBlur"
                pattern="d*"
                v-maska:unmaskedValue.unmasked="maskaOptions"
            />
            <label :for="inputId" class="ticket-price__input-label">Vklad</label>
            <span class="ticket-price__input-suffix">Kč</span>
        </div>
        <div v-html="inputErrorText" class="ticket-price__error-text" v-if="inputInvalid && !hideInputError"></div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { handleClickCapture, MIN_KOMBI_STAKE, MIN_STAKE, useTicketStore } from "@/ticket/stores/use-ticket-store";
import { vMaska } from "maska/vue";
import type { MaskInputOptions } from "maska";
import analytics from "@/shared/analytics";
import { isNumber as _isNumber } from "lodash-es";
import { TicketType } from "@/shared/model/ticket";
import useInputValidationStore from "@/shared/composables/use-input-validation";

const props = defineProps<{
    groupNumber?: number;
    hideInputError?: boolean;
    miniTicket?: boolean;
}>();

const maskaOptions: MaskInputOptions = {
    number: { locale: "cs", fraction: 2, unsigned: true },
};

const ticketStore = useTicketStore();
const { isInputInvalid, setInputInvalid } = useInputValidationStore();
const emit = defineEmits(["blur", "input"]);

const inputInvalid = computed(() => isInputInvalid.value);
const inputValue = ref<string>();
const inputElem = ref(null);
const unmaskedValue = ref<string>();
defineExpose({ unmaskedValue }); // make sure you expose the bound variable
const inputId = `bet-${Math.random().toString(36)}`;
let isSelected = false;

const validateInputPrice = () => {
    const numberableValue = Number(inputValue.value?.replace(",", "."));

    if (ticketStore.ticketType !== TicketType.KOMBI && numberableValue < MIN_STAKE) {
        setInputInvalid(true);
    } else {
        setInputInvalid(false);
    }
};

watch(
    () => (props.groupNumber >= 1 ? ticketStore.kombiStakes[props.groupNumber] : ticketStore.ticketStake),
    (newValue) => {
        if (newValue) {
            inputValue.value = _isNumber(newValue) ? newValue.toString() : undefined;
        }

        //aggresive validation if already invalid
        if (inputInvalid.value || props.miniTicket) {
            validateInputPrice();
        }
    },
    { immediate: true },
);

watch(unmaskedValue, (newValue, oldValue) => {
    // unmasked value automatically converts decimal separator to valid format (',' is converted to '.')
    if (newValue !== oldValue) {
        ticketStore.changeStake(newValue ? Number(newValue) : 0, props.groupNumber);
    }
});

const handleBlur = () => {
    // we work here with masked value (decimal separator is ',')
    if (inputValue.value) {
        inputValue.value = inputValue.value.replace(/,$/g, "");
    } else {
        inputValue.value = "0";
        unmaskedValue.value = "0";
    }
    //validate input price
    validateInputPrice();
    emit("blur");
    analytics.sendDepositChange(inputValue.value);
    isSelected = false;
};

watch(inputValue, (newValue) => {
    emit("input", newValue);
});

const transactionTotalTooLowErrors = computed(() =>
    ticketStore.errors.filter((err) =>
        ["TRANSACTION_KOMBI_TOO_LOW", "TRANSACTION_TOTAL_TOO_LOW"].includes(err.errorId),
    ),
);

const isTransactionTotalTooLowErrorRaised = ref(false);

watch(
    () => transactionTotalTooLowErrors.value.length,
    (newValue) => {
        if (newValue) {
            isTransactionTotalTooLowErrorRaised.value = true;
        }
    },
);

watch(
    () => isTransactionTotalTooLowErrorRaised.value && !ticketStore.showSendingErrors,
    (newValue) => {
        if (newValue) {
            inputElem.value.focus();
            isTransactionTotalTooLowErrorRaised.value = false;
        }
    },
);

const selectAll = (event: MouseEvent) => {
    if (isSelected) return;

    const target = event.target as HTMLInputElement;

    target.select();

    isSelected = true;
};

const inputErrorText = computed(() => {
    if (ticketStore.ticketType === TicketType.KOMBI) {
        return `Minimální vklad je ${MIN_KOMBI_STAKE}&nbsp;Kč`;
    } else {
        return `Minimální vklad je ${MIN_STAKE}&nbsp;Kč`;
    }
});
</script>
